/**
 * Patakare API Service
 * Connects React frontend to PHP API on cPanel
 */

import { getApiUrl } from './apiConfig';
import { normalizeDevUrl } from './serverConfig';
import { shouldIntercept, interceptAuth } from './authBridge';
import { apiFetch, apiErrorFromResponse, getUserErrorMessage, ApiError } from './apiError';

// Dynamic API URL based on environment
const getBaseUrl = () => getApiUrl();

// Types based on database schema
export interface Listing {
  id: number;
  name: string;
  image?: string;
  banner?: string;
  images?: string[];
  category: string;
  categoryType: number;
  location?: string;
  city: string;
  state: string;
  pincode?: string;
  address: string;
  phone?: string;
  altPhone?: string;
  email?: string;
  website?: string;
  description?: string;
  tagline?: string;
  rating?: number;
  isVerified: boolean;
  isOpen?: boolean;
  createdAt?: string;
  // Doctor specific fields
  specialization?: string;
  qualification?: string;
  experience?: number | string;
  registrationNo?: string;
  consultationFee?: number | string;
  hospitals?: string;
  clinic?: string;
  // Medical Store / Wholesaler specific fields
  drugLicense?: string;
  tinNo?: string;
  shopName?: string;
  // Diagnostic Center specific fields
  facilities?: string;
  // Hospital specific fields
  noOfBeds?: string;
  noOfIcus?: string;
  // HomeCare+ specific fields
  hospitalName?: string;
  // Social links
  social?: {
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
  };
  // Gallery
  gallery?: Array<{
    url: string;
    title?: string;
  }>;
}

export interface State {
  id: number;
  title: string;
  code?: string;
}

export interface City {
  id: number;
  title: string;
  stateId: number;
  stateName?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  user?: {
    id: number;
    username: string;
    memberType: number;
    memberTypeName: string;
    email?: string;
    phone?: string;
    token: string;
  };
}

export interface ListingsResponse {
  success: boolean;
  data: Listing[];
  type: {
    id: number;
    name: string;
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface StatsResponse {
  success: boolean;
  data: {
    users: Record<number, number>;
    totalUsers: number;
    totalCities: number;
    totalStates: number;
  };
}

// Category type mapping (from tbl_user member_type)
export const CATEGORY_TYPES = {
  HOSPITAL: 1,
  DOCTOR: 2,
  MEDICAL_STORE: 3,
  NURSE: 4,
  AMBULANCE: 5,
  DIAGNOSTIC: 6,
  WHOLESALER: 7,
} as const;

export const CATEGORY_LABELS: Record<number, string> = {
  1: 'Hospital',
  2: 'Doctor',
  3: 'Medical Store',
  4: 'HomeCare+',
  5: 'Ambulance',
  6: 'Diagnostic Center',
  7: 'Wholesaler',
  8: 'Blood Bank',
  9: 'Health Camp',
  10: 'Ambulance',
};

// Helper to normalize image URLs
// - convert dev -> production host for thumb_cache
// Database already stores filenames with correct thumb prefix (e.g., thumb_142_131_filename.jpg)
function normalizeImageUrls<T>(data: T): T {
  if (typeof data === 'string') {
    let s: string = data;
    s = normalizeDevUrl(s);
    return (s as unknown) as T;
  }
  if (Array.isArray(data)) {
    return data.map(normalizeImageUrls) as T;
  }
  if (data && typeof data === 'object') {
    const result: any = {};
    for (const key of Object.keys(data as object)) {
      result[key] = normalizeImageUrls((data as any)[key]);
    }
    return result;
  }
  return data;
}

// API Helper - using dynamic URL based on environment
async function fetchAPI<T>(action: string, params?: Record<string, string | number>, options?: RequestInit): Promise<T> {
  const searchParams = new URLSearchParams();
  searchParams.set('action', action);
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });
  }
  
  const url = `${getBaseUrl()}?${searchParams.toString()}`;
  
  const isGetRequest = !options?.method || options.method.toUpperCase() === 'GET';
  const requestOptions: RequestInit = {
    ...options,
    headers: {
      ...(isGetRequest ? {} : { 'Content-Type': 'application/json' }),
      ...options?.headers,
    },
  };

  const executeRequest = () => apiFetch(url, requestOptions);

  let response: Response;
  try {
    response = await executeRequest();
  } catch (error) {
    // A hung backend already waited out the full timeout — retrying won't help, surface it.
    if (error instanceof ApiError && error.kind === 'timeout') {
      throw error;
    }
    // Transient network hiccup / browser-cancelled request (e.g. dev-server reload) — retry once.
    response = await executeRequest();
  }

  if (!response.ok) {
    let body: { message?: string; error?: string } | null = null;
    try {
      body = await response.json();
    } catch {
      // Non-JSON error body (e.g. raw PHP fatal error page) — fall back to status-based message.
    }
    throw apiErrorFromResponse(response.status, body);
  }

  let jsonData: unknown;
  try {
    jsonData = await response.json();
  } catch {
    throw new ApiError('Server returned an invalid response. Please try again.', 'server');
  }

  // Normalize all image URLs in the response
  return normalizeImageUrls(jsonData as T);
}

// API Methods
export const api = {
  // Test connection
  async testConnection(): Promise<{ success: boolean; message: string; server_time: string; database: string }> {
    return fetchAPI('test');
  },

  // Authentication
  async login(username: string, password: string): Promise<LoginResponse> {
    if (shouldIntercept('login')) {
      try {
        return (await interceptAuth('login', { username, password })) as LoginResponse;
      } catch (err: any) {
        return { success: false, message: err?.message || 'Login failed' } as LoginResponse;
      }
    }
    try {
      return await fetchAPI<LoginResponse>('login', undefined, {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
    } catch (error) {
      return { success: false, message: getUserErrorMessage(error) } as LoginResponse;
    }
  },

  // Listings
  async getListings(params: {
    type?: number;
    city_id?: number;
    state_id?: number;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ListingsResponse> {
    try {
      return await fetchAPI('listings', params as Record<string, string | number>);
    } catch (error) {
      const fallbackLimit = Math.min(6, params.limit ?? 6);
      if ((params.limit ?? 0) > fallbackLimit) {
        return fetchAPI('listings', { ...params, limit: fallbackLimit } as Record<string, string | number>);
      }
      throw error;
    }
  },

  // Locations — unified through supabaseLocationsBridge when in Supabase mode
  async getStates(): Promise<{ success: boolean; data: State[] }> {
    const { isSupabaseAuth } = await import('./authMode');
    if (isSupabaseAuth()) {
      const loc = await import('./supabaseLocationsBridge');
      const rows = await loc.getStates();
      return { success: true, data: rows.map(r => ({ id: r.id as any, name: r.name, title: r.name } as any)) };
    }
    return fetchAPI('states');
  },

  async getCities(stateId?: number): Promise<{ success: boolean; data: City[] }> {
    const { isSupabaseAuth } = await import('./authMode');
    if (isSupabaseAuth()) {
      const loc = await import('./supabaseLocationsBridge');
      const rows = await loc.getCities(stateId ? String(stateId) : undefined);
      return { success: true, data: rows.map(r => ({ id: r.id as any, name: r.name, title: r.name, state_id: r.state_id as any, state_name: r.state_name } as any)) };
    }
    return fetchAPI('cities', stateId ? { state_id: stateId } : {});
  },

  async getPopularCities(): Promise<{ success: boolean; data: City[] }> {
    const { isSupabaseAuth } = await import('./authMode');
    if (isSupabaseAuth()) {
      const loc = await import('./supabaseLocationsBridge');
      const rows = await loc.getPopularCities();
      return { success: true, data: rows.map(r => ({ id: r.id as any, name: r.name, title: r.name, state_id: r.state_id as any, state_name: r.state_name } as any)) };
    }
    return fetchAPI('cities', { limit: 10 });
  },

  // City+category combos with enough real listings to link to (e.g.
  // /doctors/gwalior) — used for the homepage/footer "Popular Cities" SEO
  // links, distinct from getPopularCities() above (which is just an
  // admin-curated city list with no category/listing-count info).
  async getPopularCityCategories(limit = 12): Promise<{
    success: boolean;
    data: Array<{ type_code: number; category_label: string; city_name: string; path: string; count: number }>;
  }> {
    return fetchAPI('popular_city_categories', { limit });
  },

  // Profile
  async getProfile(userId: number, memberType: number): Promise<{ success: boolean; data: Record<string, unknown> }> {
    return fetchAPI('profile', { user_id: userId, member_type: memberType });
  },

  // Get Single Listing Detail
  async getListing(id: number, type?: number): Promise<{ success: boolean; data: Listing }> {
    const params: Record<string, number> = { id };
    if (type && type > 0) {
      params.type = type;
    }
    return fetchAPI('listing', params);
  },

  // Stats
  async getStats(): Promise<StatsResponse> {
    return fetchAPI('stats');
  },

  // Register new member
  async register(data: {
    username: string;
    name?: string;
    password: string;
    member_type: number;
    email?: string;
    phone?: string;
  }): Promise<LoginResponse> {
    if (shouldIntercept('register_user')) {
      try {
        return (await interceptAuth('register_user', data)) as LoginResponse;
      } catch (err: any) {
        return { success: false, message: err?.message || 'Registration failed' } as LoginResponse;
      }
    }
    try {
      return await fetchAPI<LoginResponse>('register', undefined, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      return { success: false, message: getUserErrorMessage(error) } as LoginResponse;
    }
  },

  // Forgot Password - supports email or phone
  async forgotPassword(identifier: string, extraPayload?: Record<string, string>): Promise<{ success: boolean; message: string }> {
    const payload = extraPayload ?? { email: identifier };
    if (shouldIntercept('forgot_password')) {
      try {
        return await interceptAuth('forgot_password', payload);
      } catch (err: any) {
        return { success: false, message: err?.message || 'Reset failed' };
      }
    }
    try {
      return await fetchAPI<{ success: boolean; message: string }>('forgot_password', undefined, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      return { success: false, message: getUserErrorMessage(error) };
    }
  },

  // Reset Password - With token
  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      return await fetchAPI<{ success: boolean; message: string }>('reset_password', undefined, {
        method: 'POST',
        body: JSON.stringify({ token, new_password: newPassword }),
      });
    } catch (error) {
      return { success: false, message: getUserErrorMessage(error) };
    }
  },
};

export default api;
